/**
 * Guide hero illustrations (server-safe: no hooks, no state; the AI guide's animated chat is the one client piece, GuideAiHero.tsx). One piece of art per guide, built from HTML + SVG so every word is real text.
 * Bars and positions are drawn, not measured. Names and labels come from each guide's own headings.
 * Motion is transform / opacity only and switches off under prefers-reduced-motion (see guide-art-css.ts).
 */
import React from 'react';
import { GuideAiHero } from './GuideAiHero';

/* ------------------------------------------------------------------ CRM: comparison board */
const CRMS = ['ICE Surefire', 'BNTouch', 'Jungo', 'Shape CRM', 'Total Expert', 'Lendware', 'Insellerate', 'Bonzo'];
// drawn widths (percent of the track): rating-style bars, deliberately unlabeled
const BARS: number[][] = [
  [86, 64, 72], [70, 88, 58], [92, 54, 80], [76, 82, 66], [88, 60, 90], [58, 74, 62], [82, 90, 52], [64, 70, 86],
];

function CrmBoard() {
  return (
    <div className="ga ga-crm" aria-hidden="true">
      <div className="ga-head"><span className="ga-title">Compare <b>Top 8</b></span></div>
      <div className="ga-board">
        <i className="ga-ring" />
        {CRMS.map((n, i) => (
          <div className="ga-row" key={n}>
            <span className="ga-n">{String(i + 1).padStart(2, '0')}</span>
            <span className="ga-name">{n}</span>
            <span className="ga-bars">
              {BARS[i].map((w, j) => (
                <span className="ga-track" key={j}><i style={{ width: `${w}%`, animationDelay: `${0.25 + i * 0.07 + j * 0.05}s` }} /></span>
              ))}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Marketing strategies: five-channel flywheel */
const CHANNELS = ['Paid Advertising', 'Agent Partnerships', 'Client Referrals', 'Pipeline Nurture', 'Brand & Organic'];

function Flywheel() {
  return (
    <div className="ga ga-fly" aria-hidden="true">
      <div className="ga-head"><span className="ga-title">The <b>Hybrid System</b></span></div>
      <div className="ga-wheel">
        <svg viewBox="0 0 400 400" className="ga-ringsvg" focusable="false">
          <defs>
            <linearGradient id="gaFly" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#ff34a7" /><stop offset=".55" stopColor="#7c5cff" /><stop offset="1" stopColor="#22e4ff" /></linearGradient>
          </defs>
          <circle cx="200" cy="200" r="144" fill="none" stroke="url(#gaFly)" strokeOpacity=".5" strokeWidth="2" strokeDasharray="3 9" strokeLinecap="round" />
          <circle cx="200" cy="200" r="104" fill="none" stroke="currentColor" strokeOpacity=".12" strokeWidth="1" />
        </svg>
        <div className="ga-orbit"><i /></div>
        <div className="ga-hub"><b>5</b><span>channels, one system</span></div>
        {CHANNELS.map((c, k) => {
          const a = ((-90 + 72 * k) * Math.PI) / 180;
          const x = 50 + 36 * Math.cos(a);
          const y = 50 + 36 * Math.sin(a);
          return (
            <div className="ga-node" key={c} style={{ left: `${x}%`, top: `${y}%`, animationDelay: `${(k * 14) / 5 - 0.4}s` }}>
              <span className="ga-nn">{k + 1}</span>
              <span className="ga-nl">{c}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Lead generation: shared vs owned */
function SharedVsOwned() {
  // five competing loan officers on the shared side, one on the owned side
  const spread = [-84, -42, 0, 42, 84];
  return (
    <div className="ga ga-own" aria-hidden="true">
      <div className="ga-head"><span className="ga-title"><b>3.2M</b> leads analyzed</span></div>
      <div className="ga-lanes">
        <div className="ga-lane ga-lane--dep">
          <p className="ga-lane-t">Dependency</p>
          <svg viewBox="0 0 400 130" focusable="false" className="ga-lane-svg">
            <g className="ga-lines" fill="none" stroke="currentColor" strokeOpacity=".28" strokeWidth="1.5" strokeDasharray="2 6" strokeLinecap="round">
              {spread.map((dx, i) => <path key={i} d={`M80 65 C150 65 ${230 - 0} ${65 + dx * 0.55} 300 ${65 + dx * 0.72}`} />)}
            </g>
            <circle cx="80" cy="65" r="15" className="ga-src" />
            <text x="80" y="104" textAnchor="middle" className="ga-lbl">Shared lead</text>
            {spread.map((dx, i) => <circle key={i} cx="312" cy={65 + dx * 0.72} r="9" className="ga-lo" />)}
            {spread.map((dx, i) => (
              <circle key={'p' + i} r="4.5" className="ga-pkt ga-pkt--dep" style={{ offsetPath: `path('M80 65 C150 65 230 ${65 + dx * 0.55} 300 ${65 + dx * 0.72}')`, animationDelay: `${i * 0.35}s` } as React.CSSProperties} />
            ))}
          </svg>
        </div>
        <div className="ga-lane ga-lane--own">
          <p className="ga-lane-t">Dominance</p>
          <svg viewBox="0 0 400 130" focusable="false" className="ga-lane-svg">
            <g fill="none" stroke="url(#gaOwn)" strokeOpacity=".8" strokeWidth="2.5" strokeLinecap="round">
              <path d="M60 65 H284" />
            </g>
            <defs><linearGradient id="gaOwn" gradientUnits="userSpaceOnUse" x1="60" y1="0" x2="300" y2="0"><stop offset="0" stopColor="#ff34a7" /><stop offset=".6" stopColor="#7c5cff" /><stop offset="1" stopColor="#22e4ff" /></linearGradient></defs>
            <circle cx="60" cy="65" r="13" className="ga-src ga-src--own" />
            <circle cx="320" cy="65" r="24" className="ga-you-halo" />
            <circle cx="320" cy="65" r="17" className="ga-you" />
            <text x="320" y="70" textAnchor="middle" className="ga-youlbl">You</text>
            <text x="60" y="104" textAnchor="middle" className="ga-lbl">Your lead</text>
            {[0, 1, 2].map((i) => (
              <circle key={i} r="5" className="ga-pkt ga-pkt--own" style={{ offsetPath: "path('M60 65 H300')", animationDelay: `${i * 0.9}s` } as React.CSSProperties} />
            ))}
          </svg>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ AI visibility: the animated chat lives in GuideAiHero.tsx (client) */

export function GuideHeroArt({ slug }: { slug: string }) {
  if (slug === 'best-mortgage-crm-software-2026') return <CrmBoard />;
  if (slug === 'mortgage-marketing-strategies') return <Flywheel />;
  if (slug === 'mortgage-lead-generation-apocalypse') return <SharedVsOwned />;
  if (slug === 'how-loan-officers-get-cited-by-ai') return <GuideAiHero />;
  return null;
}

/** keyframes for the CRM selection ring: eight rows, each held then eased to the next (row height is 1 in the translate unit) */
export function ringKeyframes(rows = 8): string {
  const step = 100 / rows;
  const frames: string[] = [];
  for (let i = 0; i < rows; i++) {
    const a = (i * step).toFixed(2);
    const b = ((i + 1) * step - step * 0.22).toFixed(2);
    frames.push(`${a}%,${b}%{transform:translateY(calc(var(--ga-rh)*${i}))}`);
  }
  frames.push(`100%{transform:translateY(0)}`);
  return `@keyframes ga-ring{${frames.join('')}}`;
}
